// Role-aware first-time welcome overlay + step-by-step guided tour engine.
// Restrained, keyboard-accessible, dismiss + revisit supported. Persistence and
// analytics are delegated to src/lib/onboarding.ts (privacy-safe).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, Compass, CheckCircle2 } from 'lucide-react';
import { GiisdecMark } from '@/components/portal/BrandLogo';
import {
  markTourCompleted,
  markWelcomeSeen,
  trackGuidance,
  type GuidanceRole,
} from '@/lib/onboarding';
import { ROLE_TOURS } from './guidanceContent';
import { useEscapeToClose } from './ContextualHelp';

interface GuidedTourProps {
  role: GuidanceRole;
  /** Controls whether the overlay is shown. */
  open: boolean;
  /** Start directly in tour steps (skip welcome screen). */
  startInSteps?: boolean;
  onClose: () => void;
  /** Optional: jump to a dashboard tab when a step requests it. */
  onJump?: (tab: string) => void;
}

const GuidedTour: React.FC<GuidedTourProps> = ({ role, open, startInSteps, onClose, onJump }) => {
  const tour = ROLE_TOURS[role] ?? ROLE_TOURS.public;
  const [phase, setPhase] = useState<'welcome' | 'steps'>(startInSteps ? 'steps' : 'welcome');
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (open) {
      setPhase(startInSteps ? 'steps' : 'welcome');
      setIdx(0);
      trackGuidance(startInSteps ? 'tour_started' : 'onboarding_started', { role });
    }
  }, [open, startInSteps, role]);

  const finish = useCallback(() => {
    markWelcomeSeen(role);
    markTourCompleted(role);
    onClose();
  }, [role, onClose]);

  const dismiss = useCallback(() => {
    markWelcomeSeen(role);
    trackGuidance('tour_dismissed', { role });
    onClose();
  }, [role, onClose]);

  useEscapeToClose(open, dismiss);

  // ---- Modal keyboard focus (P9G-FIX4, WCAG 2.4.3) ------------------------------------------------
  const dialogRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // Remember what had focus when the tour opened; give it back when the tour closes. An auto-opened
  // first-visit tour has no trigger (and a replay's menu item is gone by then), so focus continues at
  // the start of the page content: the <main> landmark, made programmatically focusable if needed.
  useEffect(() => {
    if (!open) return;
    const prior = document.activeElement;
    restoreRef.current = prior instanceof HTMLElement && prior !== document.body ? prior : null;
    return () => {
      const prev = restoreRef.current;
      restoreRef.current = null;
      if (prev && prev.isConnected && prev.getClientRects().length > 0) { prev.focus(); return; }
      const main = document.querySelector('main');
      if (main) {
        if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
      }
    };
  }, [open]);

  // Initial focus on the primary action, and again whenever a phase/step change removes the focused
  // control (Start -> steps, Next -> Finish), so focus is never dropped behind the dialog.
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) primaryRef.current?.focus();
  }, [open, phase, idx]);

  // Keep Tab / Shift+Tab inside the dialog while it is open; the page behind is not in the sequence.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const items = Array.from(
        dialog.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => el.getClientRects().length > 0);
      if (items.length === 0) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!dialog.contains(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const step = tour.steps[idx];
  const isLast = idx === tour.steps.length - 1;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-eds-forest/60 backdrop-blur-sm font-eds-body"
      role="dialog"
      aria-modal="true"
      aria-label={`${tour.label} orientation`}
    >
      <div className="w-full max-w-lg bg-eds-surface rounded-eds-lg shadow-eds-md overflow-hidden">
        {/* Branded gradient header */}
        <div className="relative overflow-hidden bg-eds-forest px-6 py-4 text-white">
          <div className="absolute -top-10 -right-8 w-44 h-44 rounded-eds-pill bg-eds-emerald/15 blur-3xl" aria-hidden="true" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <GiisdecMark className="w-7 h-7" />
              <span className="text-[11px] font-semibold uppercase tracking-widest text-eds-mint">
                {tour.label} guidance
              </span>
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={dismiss}
              className="text-white/70 hover:text-white hover:bg-white/10 rounded-eds-md p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-mint/60"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {phase === 'welcome' ? (
          <div className="px-6 py-8">
            <div className="mx-auto w-14 h-14 rounded-eds-pill bg-eds-success/12 border border-eds-success/25 flex items-center justify-center mb-5">
              <Compass className="w-7 h-7 text-eds-emerald-2" />
            </div>
            <h2 className="font-eds-display text-xl font-bold text-eds-fg text-center tracking-[-0.01em]">{tour.welcomeTitle}</h2>
            <p className="text-sm text-eds-muted-fg text-center mt-2.5 leading-relaxed max-w-sm mx-auto">{tour.welcomeBody}</p>
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                ref={primaryRef}
                type="button"
                onClick={() => { setPhase('steps'); trackGuidance('tour_started', { role }); }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-eds-primary hover:bg-eds-emerald-2 text-eds-primary-fg text-sm font-semibold rounded-eds-pill transition focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-surface"
              >
                Start quick tour <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="w-full sm:w-auto px-5 py-3 text-sm font-semibold text-eds-muted-fg hover:text-eds-fg rounded-eds-pill transition"
              >
                Skip for now
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-semibold text-eds-muted-fg">
                Step {idx + 1} of {tour.steps.length}
              </span>
              <div className="flex gap-1.5">
                {tour.steps.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-eds-pill transition-all ${i === idx ? 'w-6 bg-eds-emerald' : i < idx ? 'w-1.5 bg-eds-emerald/50' : 'w-1.5 bg-eds-border'}`}
                  />
                ))}
              </div>
            </div>
            <h3 className="font-eds-display text-lg font-bold text-eds-fg">{step.title}</h3>
            <p className="text-sm text-eds-muted-fg mt-2 leading-relaxed min-h-[3.5rem]">{step.body}</p>

            {step.jumpTo && onJump && (
              <button
                type="button"
                onClick={() => onJump(step.jumpTo as string)}
                className="mt-2 text-xs font-semibold text-eds-emerald-2 hover:underline inline-flex items-center gap-1"
              >
                Take me there <ArrowRight className="w-3 h-3" />
              </button>
            )}

            <div className="mt-7 flex items-center justify-between border-t border-eds-border pt-4">
              <button
                type="button"
                disabled={idx === 0}
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-eds-muted-fg disabled:opacity-40 hover:text-eds-fg transition"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              {isLast ? (
                <button
                  ref={primaryRef}
                  type="button"
                  onClick={finish}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-eds-primary hover:bg-eds-emerald-2 text-eds-primary-fg text-sm font-semibold rounded-eds-pill transition focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-surface"
                >
                  <CheckCircle2 className="w-4 h-4" /> Finish
                </button>
              ) : (
                <button
                  ref={primaryRef}
                  type="button"
                  onClick={() => setIdx((i) => Math.min(tour.steps.length - 1, i + 1))}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-eds-primary hover:bg-eds-emerald-2 text-eds-primary-fg text-sm font-semibold rounded-eds-pill transition focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-surface"
                >
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuidedTour;
